import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Kitchen-sink visual Alert (title + description + semantic variants).
 *
 * Distinct from `@/components/ui/alert` which is the telemetry notification
 * wrapper (surface/kind). Use this for inline error/success/info callouts.
 * Source: HRCRS/crucible kitchen-sink FeedbackSection.
 */

const inlineAlertVariants = cva(
  // Grid: optional leading icon + stacked title/description. Containment via
  // surface fill, not a border (DESIGN_SYSTEM §4).
  'relative grid w-full grid-cols-[0_1fr] gap-y-1 rounded-xl px-4 py-3 text-body has-[>svg]:grid-cols-[1.25rem_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5',
  {
    variants: {
      variant: {
        default: 'bg-surface-2 text-secondary [&>svg]:text-tertiary',
        info: 'bg-info-muted text-secondary [&>svg]:text-semantic-info',
        success: 'bg-success-muted text-secondary [&>svg]:text-semantic-success',
        warning: 'bg-warning-muted text-secondary [&>svg]:text-semantic-warning',
        error: 'bg-error-muted text-secondary [&>svg]:text-semantic-error',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function InlineAlert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inlineAlertVariants>) {
  return (
    <div
      data-slot="inline-alert"
      role="alert"
      className={cn(inlineAlertVariants({ variant }), className)}
      {...props}
    />
  )
}

function InlineAlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="inline-alert-title"
      className={cn('col-start-2 text-label font-medium text-[var(--text-primary)]', className)}
      {...props}
    />
  )
}

function InlineAlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="inline-alert-description"
      className={cn('col-start-2 text-caption text-tertiary', className)}
      {...props}
    />
  )
}

export { InlineAlert, InlineAlertTitle, InlineAlertDescription, inlineAlertVariants }
